import type { Competitor } from '../types';

// Researched 2026-11-03. Every claim links to the page it came from.
// To correct something: change it here, update sourceUrl and lastReviewed.

export const daasity: Competitor = {
  "id": "daasity",
  "name": "Daasity",
  "group": "ecomm",
  "website": "https://www.daasity.com/",
  "owner": "Independent, privately held (San Diego, CA; founded 2017; no acquisition of Daasity found). Tim Vollman named CEO in 2025.",
  "summary": "Daasity is a managed ELT and data-model product for consumer brands: it extracts Shopify, Amazon, marketing and retail data into a Snowflake or BigQuery warehouse, maintains unified commerce schemas on top, and serves prebuilt dashboards through a Daasity-managed Looker instance. It targets omnichannel brands (DTC, marketplace and retail/wholesale) that want warehouse-grade data without building pipelines themselves.",
  "bestFor": [
    "Omnichannel consumer brands selling DTC, on Amazon and into retail that want one warehouse with prebuilt unified order, retail and marketing models",
    "CPG brands that need syndicated retail data (SPINS, NielsenIQ) or retailer/distributor portal data alongside Shopify and Amazon",
    "Teams with some SQL skill that want full warehouse access but would rather not build and maintain extractors and core models themselves"
  ],
  "notFor": [
    "Small Shopify-only stores, given entry pricing in the $1,499-$1,999/month range",
    "Brands that want TikTok Shop as a standard connector today (it is in beta and enabled per account)",
    "Teams that want to keep their existing BI tool as the main front end; Daasity's dashboards run on a Daasity-managed Looker instance and Tableau/Power BI support for its AI Analyst is only on the roadmap"
  ],
  "strengths": [
    "Extractors and unified commerce schemas (orders, retail, marketing, and others) are built and maintained by Daasity, work a consultant would otherwise have to build in dbt and keep running",
    "Data lands in a Snowflake or BigQuery warehouse with full SQL access, so the brand can still query and extend it, unlike closed ecommerce dashboards",
    "Prebuilt connectors for hard-to-get retail sources: SPINS and NielsenIQ syndicated data, Whole Foods and Kroger portals, and UNFI/KeHE distributor data",
    "Ready-made report library (flash, cohort, attribution, inventory, promotion analysis) on day one, backed by a 30-day onboarding guarantee on all plans",
    "Reverse-ETL audiences that push segments to Meta, Google, Klaviyo, Attentive and Braze without a separate activation tool",
    "AI Analyst add-on answers plain-English questions from governed metrics in-app or via an MCP connector for Claude, ChatGPT, Cursor and Codex"
  ],
  "limitations": [
    "TikTok Shop integration is in beta and must be enabled per account by Daasity",
    "NetSuite and retailer TPM data are listed as enterprise add-ons rather than standard connectors",
    "Dashboards are delivered through a Looker instance that Daasity provisions and manages; customization beyond the base setup is done in LookML",
    "AI Analyst is a separately licensed add-on, is read-only, and does not build dashboards; Tableau and Power BI support is listed as roadmap",
    "Public pricing starts at $1,499-$1,999/month and enterprise customization is quote-only; a competitor comparison (SourceMedium) also cites user reports of extra costs for custom setup (third-party, unverified)"
  ],
  "pricing": {
    "model": "Flat monthly tiers on Daasity's site; Shopify App Store listing says usage charges are based on a rolling 3-month average of annualized revenue",
    "publicStartingPrice": "Starter Essentials $1,499/month (Daasity pricing page); Shopify App Store listing shows from $1,899/month",
    "freeTier": "No free tier found on Daasity's own pricing page; Shopify App Store listing offers a 14-day free trial",
    "notes": "Daasity pricing page lists Starter Essentials $1,499/mo (native connections, dashboards, reports), Essentials $1,999/mo (full data foundation with contribution margin), Enterprise custom (customization and consulting, for retail + DTC + digital brands), and a limited-time Syndicated Data + AI Analyst offer at $499/mo. All plans include a 30-day onboarding guarantee. The Shopify App Store listing separately shows from $1,899/month with revenue-based usage charges; PricingSaaS logged the listing's starting price rising from $399 to $1,899/month in April 2026. Warehouse compute/storage cost when using your own Snowflake or BigQuery: unknown (not stated). Setup time: Daasity offers a 30-day onboarding guarantee; no typical setup duration published.",
    "sourceUrl": "https://www.daasity.com/pricing-2026",
    "checkedOn": "2026-11-03"
  },
  "features": {
    "shopify": {
      "value": "yes",
      "note": "Native Shopify / Shopify Plus extractor",
      "sourceUrl": "https://help.daasity.com/core-concepts/data-integrations/popular-setups"
    },
    "amazon": {
      "value": "yes",
      "note": "Amazon listed among core commerce integrations",
      "sourceUrl": "https://help.daasity.com/core-concepts/data-integrations/popular-setups"
    },
    "tiktok_shop": {
      "value": "partial",
      "note": "Orders, finance, returns, products, promotions extracted, but integration is in beta and enabled per account",
      "sourceUrl": "https://help.daasity.com/core-concepts/data-integrations/setup-guides/digital-integrations/tiktok-shop/integration-specifications",
      "badge": "Beta"
    },
    "wholesale_retail_or_erp": {
      "value": "yes",
      "note": "SPINS, NielsenIQ, Whole Foods/Kroger portals, UNFI/KeHE; NetSuite and retailer TPM as enterprise add-ons",
      "sourceUrl": "https://help.daasity.com/core-concepts/data-integrations/popular-setups"
    },
    "ad_platforms": {
      "value": "yes",
      "note": "Meta Ads and Google Ads listed; TikTok ads not confirmed in pages opened",
      "sourceUrl": "https://help.daasity.com/core-concepts/data-integrations/popular-setups"
    },
    "first_party_pixel_attribution": {
      "value": "partial",
      "note": "Eight attribution models incl. last-click, first-click, assisted and survey-based; no own tracking pixel described",
      "sourceUrl": "https://daasity.com/attribution"
    },
    "mmm_incrementality": {
      "value": "unknown",
      "note": "Not mentioned on attribution page; a Promotion Predictor was in testing in Sept 2025",
      "sourceUrl": "https://www.daasity.com/post/from-dashboards-to-ai-analysts-how-daasity-is-transforming-analytics"
    },
    "profit_cogs": {
      "value": "yes",
      "note": "Contribution margin built into the Essentials plan data foundation",
      "sourceUrl": "https://www.daasity.com/pricing-2026"
    },
    "ltv_cohorts": {
      "value": "yes",
      "note": "Cohort dashboards in the prebuilt report library",
      "sourceUrl": "https://help.daasity.com/start-here/core-concepts"
    },
    "subscription_analytics": {
      "value": "yes",
      "note": "Recharge listed as a standard integration",
      "sourceUrl": "https://help.daasity.com/core-concepts/data-integrations/popular-setups"
    },
    "warehouse_access": {
      "value": "yes",
      "note": "Connects to your Snowflake or BigQuery warehouse; docs state full SQL access",
      "sourceUrl": "https://help.daasity.com/getting-started/installation/1-connect-your-warehouse.md"
    },
    "custom_metrics": {
      "value": "yes",
      "note": "Custom fields, calculated metrics and custom data marts supported",
      "sourceUrl": "https://help.daasity.com/getting-started/philosophy.md"
    },
    "custom_sql": {
      "value": "yes",
      "note": "Full SQL access for custom reports, metrics and data marts",
      "sourceUrl": "https://help.daasity.com/getting-started/philosophy.md"
    },
    "ai_assistant": {
      "value": "yes",
      "note": "AI Analyst add-on: in-app chat plus read-only MCP connector; separately licensed",
      "sourceUrl": "https://help.daasity.com/core-concepts/ai-analyst"
    },
    "mobile_app": {
      "value": "unknown",
      "note": "No mobile app found in pages reviewed",
      "sourceUrl": "https://help.daasity.com/start-here/core-concepts"
    }
  },
  "recentChanges": [
    {
      "date": "2026-04",
      "change": "Shopify App Store starting price changed from $399/month to $1,899/month (logged by PricingSaaS)",
      "sourceUrl": "https://pricingsaas.com/companies/daasity/diffs/2026Q2"
    },
    {
      "date": "2026",
      "change": "Published plans: Starter Essentials $1,499/mo, Essentials $1,999/mo, Enterprise custom, plus Syndicated Data + AI Analyst at $499/mo",
      "sourceUrl": "https://www.daasity.com/pricing-2026"
    },
    {
      "date": "2026",
      "change": "AI Analyst add-on documented with in-app chat and an MCP connector for Claude, ChatGPT, Cursor and Codex CLI",
      "sourceUrl": "https://help.daasity.com/core-concepts/ai-analyst"
    },
    {
      "date": "2025-09",
      "change": "Tim Vollman named CEO; Daasity One redesigned platform launched; conversational AI analyst and Promotion Predictor in testing",
      "sourceUrl": "https://www.daasity.com/post/from-dashboards-to-ai-analysts-how-daasity-is-transforming-analytics"
    },
    {
      "date": "2024-11",
      "change": "Direct SPINS syndicated retail data integration announced",
      "sourceUrl": "https://www.daasity.com/post/daasity-spins-retail-analytics"
    }
  ],
  "sources": [
    {
      "title": "Daasity Pricing",
      "url": "https://www.daasity.com/pricing-2026"
    },
    {
      "title": "Daasity Reporting & Analytics on Shopify App Store",
      "url": "https://apps.shopify.com/daasity?locale=nl"
    },
    {
      "title": "PricingSaaS: Daasity 2026 Q2 pricing diff (third-party)",
      "url": "https://pricingsaas.com/companies/daasity/diffs/2026Q2"
    },
    {
      "title": "G2: Daasity pricing (third-party)",
      "url": "https://www.g2.com/products/daasity/pricing"
    },
    {
      "title": "Daasity Core Concepts",
      "url": "https://help.daasity.com/start-here/core-concepts"
    },
    {
      "title": "Daasity Philosophy",
      "url": "https://help.daasity.com/getting-started/philosophy.md"
    },
    {
      "title": "Connect Your Warehouse",
      "url": "https://help.daasity.com/getting-started/installation/1-connect-your-warehouse.md"
    },
    {
      "title": "Snowflake warehouse setup",
      "url": "https://help.daasity.com/warehouse/snowflake"
    },
    {
      "title": "Looker in Daasity",
      "url": "https://help.daasity.com/core-concepts/bi-tools/looker"
    },
    {
      "title": "Popular Setups (integrations)",
      "url": "https://help.daasity.com/core-concepts/data-integrations/popular-setups"
    },
    {
      "title": "TikTok Shop integration specifications",
      "url": "https://help.daasity.com/core-concepts/data-integrations/setup-guides/digital-integrations/tiktok-shop/integration-specifications"
    },
    {
      "title": "Daasity Attribution",
      "url": "https://daasity.com/attribution"
    },
    {
      "title": "Daasity AI Analyst",
      "url": "https://help.daasity.com/core-concepts/ai-analyst"
    },
    {
      "title": "From Dashboards to AI Analysts (Daasity blog, Sept 2025)",
      "url": "https://www.daasity.com/post/from-dashboards-to-ai-analysts-how-daasity-is-transforming-analytics"
    },
    {
      "title": "Daasity + SPINS (Daasity blog, Nov 2024)",
      "url": "https://www.daasity.com/post/daasity-spins-retail-analytics"
    },
    {
      "title": "SourceMedium: Daasity alternative (competitor page, third-party)",
      "url": "https://sourcemedium-www.pages.dev/compare/daasity-alternative"
    }
  ],
  "lastReviewed": "2026-11-03"
};
